import { useEffect, useState } from 'react'

// Minimum time the loader stays visible so it never "flashes" when the page is
// already cached, plus a hard safety cap so we never trap the user behind it.
const MIN_DISPLAY_MS = 500
const DOOR_DURATION_MS = 900
const MAX_WAIT_MS = 8000

export default function PageLoader() {
  const [isOpen, setIsOpen] = useState(false)
  const [isRemoved, setIsRemoved] = useState(false)

  // Open the doors once the page content has finished loading.
  useEffect(() => {
    const mountedAt = Date.now()
    let revealTimer: number | undefined
    let maxTimer: number | undefined

    const reveal = () => {
      const elapsed = Date.now() - mountedAt
      const wait = Math.max(0, MIN_DISPLAY_MS - elapsed)
      revealTimer = window.setTimeout(() => setIsOpen(true), wait)
    }

    if (document.readyState === 'complete') {
      reveal()
    } else {
      window.addEventListener('load', reveal, { once: true })
    }

    // Safety net: never keep the overlay longer than MAX_WAIT_MS.
    maxTimer = window.setTimeout(() => setIsOpen(true), MAX_WAIT_MS)

    return () => {
      window.removeEventListener('load', reveal)
      window.clearTimeout(revealTimer)
      window.clearTimeout(maxTimer)
    }
  }, [])

  // Remove the overlay from the DOM once the door animation has played.
  useEffect(() => {
    if (!isOpen) return
    const timer = window.setTimeout(() => setIsRemoved(true), DOOR_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [isOpen])

  if (isRemoved) return null

  const doorTransition =
    'transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)]'

  return (
    <div
      aria-hidden
      className={`fixed inset-0 z-[9999] overflow-hidden ${
        isOpen ? 'pointer-events-none' : ''
      }`}
    >
      {/* Left door panel — its background spans the full viewport and is
          anchored to the left edge, so it lines up seamlessly with the right
          panel while both are closed. */}
      <div
        className={`absolute inset-y-0 left-0 w-1/2 overflow-hidden ${doorTransition} ${
          isOpen ? '-translate-x-full' : 'translate-x-0'
        }`}
      >
        <div className="absolute inset-y-0 left-0 w-screen bg-gradient" />
      </div>
      {/* Right door panel — full-viewport background anchored to the right
          edge, mirroring the left panel for an invisible seam. */}
      <div
        className={`absolute inset-y-0 right-0 w-1/2 overflow-hidden ${doorTransition} ${
          isOpen ? 'translate-x-full' : 'translate-x-0'
        }`}
      >
        <div className="absolute inset-y-0 right-0 w-screen bg-gradient" />
      </div>

      {/* Brand mark, fades out as the doors start to open */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
          isOpen ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <img
          src="/img/logos/shelters/logo-192x192-min.png"
          alt=""
          className="relative h-24 w-24 animate-pulse rounded-card shadow-[0_18px_42px_rgba(0,0,0,0.35)] tablet:h-28 tablet:w-28"
        />
      </div>
    </div>
  )
}
